import {
  CommandPalette,
  type PaletteCommand,
  type PaletteMode,
  type PaletteRemoteSearch,
  type PaletteSearchItem,
  plainMarkup,
} from '@patchbay/ui'
import { useQuery } from '@tanstack/react-query'
import { useNavigate, useRouter, useRouterState } from '@tanstack/react-router'
import {
  BadgeCheck,
  BarChart3,
  BookOpen,
  Coins,
  FilePlus2,
  Home,
  Inbox,
  Keyboard,
  Lightbulb,
  ListChecks,
  Moon,
  Send,
  Sparkles,
  TrendingUp,
  Truck,
} from 'lucide-react'
import * as React from 'react'
import { useMe } from '../features/auth/session'
import { useBrandScope } from '../features/brands/use-brand-scope'
import { helpPaletteCommands } from '../features/help/palette'
import { canSeeLicences } from '../features/licences/common'
import { canSeeProfit } from '../features/profit/api'
import { describeError } from '../lib/api/client'
import type { SearchHit } from '../lib/api/generated'
import { searchQuery } from '../lib/api/queries'
import { brandFeatures } from '../lib/brand-features'
import { goToCase, goToHref } from '../lib/go-to'
import { KEYS } from '../lib/keys'
import { openSearchHit } from '../lib/search-hit'
import { applyTheme } from '../lib/theme'
import { unreadOf } from '../lib/unread'
import { useWorkspace } from '../lib/workspace'
import { type AskActions, askRiley } from './palette-ask'

/** Debounced server search for the palette; `error` is shown as "Search unreachable", never "nothing matches". */
function useRemoteSearch(query: string, mode: PaletteMode, onPick: (hit: SearchHit) => void): PaletteRemoteSearch {
  const [debounced, setDebounced] = React.useState('')
  React.useEffect(() => {
    const t = setTimeout(() => setDebounced(mode === 'ask' ? '' : query.trim()), 200)
    return () => clearTimeout(t)
  }, [query, mode])
  const q = useQuery({ ...searchQuery(debounced), enabled: debounced.length >= 2 })
  if (debounced.length < 2) return { status: 'idle', items: [] }
  if (q.isPending) return { status: 'loading', items: [] }
  if (q.isError) return { status: 'error', items: [], message: describeError(q.error) }
  return {
    status: 'ok',
    items: q.data.map((h) => ({
      id: h.id,
      kind: h.kind,
      label: plainMarkup(h.label),
      detail: h.sublabel ? plainMarkup(h.sublabel) : undefined,
      status: h.status ?? undefined,
      closed: h.closed ?? undefined,
      unread: unreadOf(h)?.label,
      onSelect: () => onPick(h),
    })),
  }
}

export function AppPalette() {
  const ws = useWorkspace()
  const navigate = useNavigate()
  const router = useRouter()
  const inCase = ws.scope
  const me = useMe()
  const brandScope = useBrandScope()
  const enabledFeatures = brandFeatures(me, brandScope.selected)
  const canDispatch = enabledFeatures.has('work_orders') && enabledFeatures.has('schedule')
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  const [typed, setTyped] = React.useState<{ query: string; mode: PaletteMode }>({ query: '', mode: 'command' })
  const onQueryChange = React.useCallback((query: string, mode: PaletteMode) => setTyped({ query, mode }), [])
  const remote = useRemoteSearch(typed.query, typed.mode, (hit) =>
    // goToHref: picking the page already on screen still reloads it and scrolls to the top (issue #98).
    openSearchHit(hit, ({ href }) => goToHref(router, href)),
  )

  const commands: PaletteCommand[] = [
    ...(inCase && canDispatch
      ? [
          {
            id: 'dispatch',
            group: `In ${inCase.caseId}`,
            label: 'Schedule',
            icon: <Send />,
            keys: KEYS.dispatch.keys,
            onSelect: () => navigate({ to: '/case/$id', params: { id: inCase.caseId }, search: { dispatch: true } }),
          },
        ]
      : []),
    {
      id: 'new-case',
      group: 'Create',
      label: 'New case',
      icon: <FilePlus2 />,
      keywords: ['create', 'open', 'start', 'ticket', 'wizard'],
      keys: KEYS.newCase.keys,
      onSelect: () => ws.setNewCaseOpen(true),
    },
    { id: 'go-home', group: 'Go to', label: 'Home', icon: <Home />, keys: KEYS.goHome.keys, onSelect: () => navigate({ to: '/' }) },
    {
      id: 'go-queue',
      group: 'Go to',
      label: 'Queue · Needs us now',
      icon: <Inbox />,
      keys: KEYS.goQueue.keys,
      onSelect: () => navigate({ to: '/queue/$lane', params: { lane: 'needs-us-now' } }),
    },
    ...(enabledFeatures.has('schedule')
      ? [
          {
            id: 'go-dispatch',
            group: 'Go to',
            label: 'Schedule',
            icon: <Truck />,
            keys: KEYS.goDispatch.keys,
            onSelect: () => navigate({ to: '/schedule' }),
          },
        ]
      : []),
    ...(enabledFeatures.has('billing')
      ? [
          {
            id: 'go-billing',
            group: 'Go to',
            label: 'Ready to bill',
            icon: <ListChecks />,
            keys: KEYS.goBilling.keys,
            onSelect: () => navigate({ to: '/billing' }),
          },
        ]
      : []),
    {
      id: 'go-reports',
      group: 'Go to',
      label: 'Reports',
      icon: <BarChart3 />,
      keywords: ['analytics', 'metrics', 'dashboard', 'kpi'],
      keys: KEYS.goReports.keys,
      onSelect: () => navigate({ to: '/reports' }),
    },
    ...(enabledFeatures.has('billing') && canSeeProfit(me?.role)
      ? [
          {
            id: 'go-profit',
            group: 'Go to',
            label: 'Profitability',
            icon: <TrendingUp />,
            keys: KEYS.goProfit.keys,
            onSelect: () => navigate({ to: '/profit' }),
          },
        ]
      : []),
    ...(enabledFeatures.has('licences') && canSeeLicences(me?.role)
      ? [{ id: 'go-licences', group: 'Go to', label: 'Licences', icon: <BadgeCheck />, onSelect: () => navigate({ to: '/licences' }) }]
      : []),
    ...(me?.role === 'manager'
      ? [
          {
            id: 'go-ai-spend',
            group: 'Go to',
            label: 'AI spend',
            icon: <Coins />,
            onSelect: () => navigate({ to: '/admin/ai-spend' }),
          },
        ]
      : []),
    ...(enabledFeatures.has('billing')
      ? [
          {
            id: 'go-ledger',
            group: 'Go to',
            label: 'Ledger',
            icon: <BookOpen />,
            keys: KEYS.goLedger.keys,
            onSelect: () => navigate({ to: '/ledger' }),
          },
        ]
      : []),
    {
      id: 'go-knowledge',
      group: 'Go to',
      label: 'Knowledge',
      icon: <Lightbulb />,
      keys: KEYS.goKnowledge.keys,
      onSelect: () => navigate({ to: '/knowledge' }),
    },
    {
      id: 'keys',
      group: 'Help',
      label: 'Keyboard shortcuts',
      icon: <Keyboard />,
      keys: KEYS.keySheet.keys,
      onSelect: () => ws.setKeySheetOpen(true),
    },
    // "Help for this page" (when the page has a guide) and "Help centre" (slice 36, #288).
    ...helpPaletteCommands({ pathname, go: (href) => navigate({ href }) }),
    {
      id: 'riley-full',
      group: 'Riley',
      label: 'Open Riley full-size',
      icon: <Sparkles />,
      keys: KEYS.rileyFull.keys,
      onSelect: () => ws.setRileyMode('full'),
    },
    { id: 'theme-dark', group: 'View', label: 'Dark theme', icon: <Moon />, keywords: ['theme'], onSelect: () => applyTheme('dark') },
    { id: 'theme-light', group: 'View', label: 'Light theme', icon: <Moon />, keywords: ['theme'], onSelect: () => applyTheme('light') },
  ]

  // Cases, tickets, contractors and people come from the server search (`remote`); nothing is listed locally.
  const searchItems: PaletteSearchItem[] = []

  const askActions: AskActions = {
    openCase: (caseNumber) => void goToCase(router, caseNumber),
    openRail: () => ws.setRileyMode('rail'),
    goHome: () => navigate({ to: '/' }),
  }
  const onAsk = (question: string) =>
    askRiley(question, inCase ? { kind: 'case', caseNumber: inCase.caseId } : { kind: 'queue' }, askActions)

  return (
    <CommandPalette
      open={ws.paletteOpen}
      onOpenChange={ws.setPaletteOpen}
      commands={commands}
      searchItems={searchItems}
      onAsk={onAsk}
      remote={remote}
      onQueryChange={onQueryChange}
      scope={inCase ? `in ${inCase.caseId} · ${inCase.customer}` : 'Everywhere'}
    />
  )
}
